import React, { useEffect, useState } from 'react';
import ClientContextLine from '../components/ClientContextLine';
import { useData } from '../contexts/DataContext';
import { adminApi } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { can } from '../components/PaymentActions';
import {
  MagnifyingGlassIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  EyeIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const CATEGORY_STYLES = {
  auth: 'bg-blue-100 text-blue-800',
  order: 'bg-green-100 text-green-800',
  payment: 'bg-purple-100 text-purple-800',
  account: 'bg-gray-100 text-gray-800',
  shopping: 'bg-yellow-100 text-yellow-800',
  admin: 'bg-red-100 text-red-800',
  other: 'bg-gray-100 text-gray-700',
};
const prettyType = (t) => (t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Account status badge colors (matches backend users.account_status values).
const ACCOUNT_STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  on_hold: 'bg-orange-100 text-orange-800',
  suspended: 'bg-red-100 text-red-800',
};
const ACCOUNT_STATUS_LABELS = { active: 'Active', on_hold: 'On hold', suspended: 'Suspended' };

// Small badge for the customers list. Renders nothing for active accounts to avoid clutter;
// flags held/suspended accounts at a glance (the list now returns account_status).
const AccountStatusBadge = ({ status }) => {
  if (!status || status === 'active') return null;
  return (
    <span className={`ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${ACCOUNT_STATUS_STYLES[status] || ACCOUNT_STATUS_STYLES.active}`}>
      {ACCOUNT_STATUS_LABELS[status] || status}
    </span>
  );
};
const DEFAULT_HOLD_REASON = 'High purchase volume — account verification required';

// Risk badge + signal colors for the per-customer shipments view.
const RISK_STYLES = {
  high: 'bg-red-100 text-red-800',
  elevated: 'bg-amber-100 text-amber-800',
  low: 'bg-green-100 text-green-800',
};
const SIGNAL_STYLES = { high: 'text-red-700', medium: 'text-amber-700' };

// Turn a Shippo address object (jsonb) into display lines.
const fmtAddressLines = (addr, fallbackName) => {
  const o = addr && typeof addr === 'object' ? addr : {};
  const name = fallbackName || o.name || '—';
  const street = [o.street1, o.street2].filter(Boolean).join(', ');
  const cityLine = [o.city, o.state, o.zip].filter(Boolean).join(', ');
  return [name, street, cityLine, o.country].filter(Boolean);
};

const Customers = () => {
  const { customers, loading, refreshData, getCustomerActivity } = useData();
  const { user } = useAuth();
  const role = user?.role || 'viewer';
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const handleExport = async () => {
    try {
      const res = await adminApi.exportCustomers();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'customers.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshData();
    setRefreshing(false);
  };

  const filteredCustomers = customers?.filter(customer =>
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone?.includes(searchTerm)
  ) || [];

  const CustomerModal = ({ customer, onClose }) => {
    const [activity, setActivity] = useState([]);
    const [activityLoading, setActivityLoading] = useState(false);
    // The list row lacks account_status; the detail endpoint returns it. Fetch detail on open.
    const [detail, setDetail] = useState(customer);
    const [holdBusy, setHoldBusy] = useState(false);
    const [reason, setReason] = useState(DEFAULT_HOLD_REASON);
    const [verifyBusy, setVerifyBusy] = useState(false);
    const [verifyDeadline, setVerifyDeadline] = useState(5);
    const [verifyHold, setVerifyHold] = useState(true);
    const [voidBusy, setVoidBusy] = useState(false);
    const [voidResult, setVoidResult] = useState(null);
    const [voidError, setVoidError] = useState('');
    const [walletBusy, setWalletBusy] = useState(false);
    const [walletAmt, setWalletAmt] = useState('');
    const [walletReason, setWalletReason] = useState('');
    const [shipments, setShipments] = useState([]);
    const [risk, setRisk] = useState(null);
    const [shipmentsLoading, setShipmentsLoading] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const status = detail?.account_status || 'active';

    useEffect(() => {
      let cancelled = false;
      if (!customer?.id) return undefined;
      setActivityLoading(true);
      getCustomerActivity(customer.id, { pageSize: 50 })
        .then((res) => { if (!cancelled) setActivity(res?.data || []); })
        .catch(() => { if (!cancelled) setActivity([]); })
        .finally(() => { if (!cancelled) setActivityLoading(false); });
      adminApi.getCustomerDetails(customer.id)
        .then((d) => { if (!cancelled && d) setDetail(d); })
        .catch(() => { /* keep list-row data */ });
      setShipmentsLoading(true);
      adminApi.getCustomerShipments(customer.id, { limit: 500 })
        .then((res) => { if (!cancelled) { setShipments(res?.shipments || []); setRisk(res?.risk || null); } })
        .catch(() => { if (!cancelled) { setShipments([]); setRisk(null); } })
        .finally(() => { if (!cancelled) setShipmentsLoading(false); });
      return () => { cancelled = true; };
    }, [customer?.id]);

    const refreshDetail = async () => {
      try { const d = await adminApi.getCustomerDetails(customer.id); if (d) setDetail(d); } catch { /* noop */ }
    };

    // Two-step: count first (dryRun), then act. Voiding cannot be undone, so the operator
    // sees how many labels are affected before confirming.
    const handleVoidUnused = async (dryRun) => {
        setVoidBusy(true); setVoidError(''); if (dryRun) setVoidResult(null);
        try {
            const res = await adminApi.voidUnusedLabels(customer.email, { dryRun });
            setVoidResult(res);
        } catch (e) {
            setVoidError(e?.response?.data?.message || 'Could not void labels for this account.');
        } finally {
            setVoidBusy(false);
        }
    };

    const handleHold = async () => {
      if (!reason.trim()) { toast.warn('Enter a reason for the hold (internal only)'); return; }
      setHoldBusy(true);
      try {
        await adminApi.holdCustomerAccount(customer.id, reason.trim());
        toast.success('Account placed on hold — label purchases are now blocked');
        await refreshDetail();
        refreshData();
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Failed to place account on hold');
      } finally { setHoldBusy(false); }
    };

    const handleUnhold = async () => {
      setHoldBusy(true);
      try {
        await adminApi.unholdCustomerAccount(customer.id);
        toast.success('Account hold released — customer reactivated');
        await refreshDetail();
        refreshData();
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Failed to release account hold');
      } finally { setHoldBusy(false); }
    };

    const handleWalletAdjust = async () => {
      const amt = parseFloat(walletAmt);
      if (!amt || amt === 0) { toast.warn('Enter a non-zero amount (negative to debit)'); return; }
      setWalletBusy(true);
      try {
        const res = await adminApi.adjustWallet(customer.id, amt, walletReason.trim());
        toast.success(`Wallet adjusted — new balance $${Number(res.balanceAfterUsd).toFixed(2)}`);
        setWalletAmt(''); setWalletReason('');
        await refreshDetail();
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Wallet adjustment failed');
      } finally { setWalletBusy(false); }
    };

    const handleRequestVerification = async () => {
      setVerifyBusy(true);
      try {
        const res = await adminApi.requestVerification(customer.id, { hold: verifyHold, deadlineDays: Number(verifyDeadline) || 5 });
        if (res?.emailed) toast.success(`Verification email sent to ${res.email}${res.held ? ' · account held' : ''}`);
        else toast.warn(`Verification ${res?.held ? 'hold set, but the ' : ''}email could not be sent (check mailer config)`);
        await refreshDetail();
        refreshData();
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Failed to request verification');
      } finally { setVerifyBusy(false); }
    };

    if (!customer) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-4 sm:top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
          <div className="mt-3">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Customer Details</h3>
              <button
                onClick={onClose}
                className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{customer.name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{customer.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <p className="mt-1 text-sm text-gray-900">{customer.phone || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Customer Since</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              {customer.shipping_addresses && customer.shipping_addresses.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Shipping Addresses</label>
                  <div className="space-y-2">
                    {customer.shipping_addresses.map((address, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-md">
                        <p className="text-sm text-gray-900">
                          {address.street}, {address.city}, {address.state} {address.zip}
                        </p>
                        {address.country && (
                          <p className="text-xs text-gray-500">{address.country}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Order History</label>
                <p className="mt-1 text-sm text-gray-900">
                  {customer.total_orders || 0} orders • ${customer.total_spent || '0.00'} total spent
                </p>
              </div>

              {/* Wallet (store credit) — balance + manual adjustment. Gated by the Zelle flag. */}
              {process.env.REACT_APP_ENABLE_ZELLE_FUNDING === 'true' && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">Wallet balance (store credit)</label>
                    <span className="text-sm font-semibold text-gray-900">${Number(detail?.wallet_balance_usd || 0).toFixed(2)}</span>
                  </div>
                  {can(role, 'finance') ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input type="number" step="0.01" value={walletAmt} onChange={(e) => setWalletAmt(e.target.value)}
                        placeholder="+ credit / - debit"
                        className="w-32 text-sm border border-gray-300 rounded px-2 py-1" />
                      <input type="text" value={walletReason} onChange={(e) => setWalletReason(e.target.value)}
                        placeholder="Reason (internal)"
                        className="flex-1 min-w-[140px] text-sm border border-gray-300 rounded px-2 py-1" />
                      <button onClick={handleWalletAdjust} disabled={walletBusy}
                        className="min-h-[40px] px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50">
                        {walletBusy ? 'Working…' : 'Adjust'}
                      </button>
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-gray-400">Requires the finance role to adjust.</p>
                  )}
                </div>
              )}

              {/* Shipments + risk signals (support + fraud view) */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Shipments{risk?.metrics?.orders ? ` (${risk.metrics.orders})` : ''}
                  </label>
                  {risk && (
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${RISK_STYLES[risk.level] || RISK_STYLES.low}`}>
                      Risk: {prettyType(risk.level)}{typeof risk.score === 'number' ? ` (${risk.score})` : ''}
                    </span>
                  )}
                </div>

                {risk?.signals?.length > 0 && (
                  <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs font-semibold text-amber-800 mb-1">Risk signals</p>
                    <ul className="space-y-1">
                      {risk.signals.map((s, i) => (
                        <li key={i} className={`text-xs ${SIGNAL_STYLES[s.level] || 'text-gray-700'}`}>⚠ {s.label}</li>
                      ))}
                    </ul>
                    {risk.metrics && (
                      <p className="text-[11px] text-gray-500 mt-2">
                        {risk.metrics.distinct_senders} senders • {risk.metrics.distinct_recipients} recipients • {risk.metrics.ship_to_states} states • peak {risk.metrics.peak_24h}/24h • {risk.metrics.distinct_cards} card(s)
                        {risk.metrics.signup_to_first_order_hours != null ? ` • first order ${risk.metrics.signup_to_first_order_hours}h after signup` : ''}
                      </p>
                    )}
                  </div>
                )}

                {shipmentsLoading ? (
                  <div className="flex items-center text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                    Loading shipments…
                  </div>
                ) : shipments.length === 0 ? (
                  <p className="text-sm text-gray-500">No shipments.</p>
                ) : (
                  <ul className="max-h-80 overflow-y-auto border border-gray-100 rounded-md divide-y divide-gray-100">
                    {shipments.map((s) => {
                      const expanded = expandedId === s.id;
                      return (
                        <li
                          key={s.id}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => setExpandedId(expanded ? null : s.id)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-gray-900 truncate">
                                <span className="font-medium">{s.from_name || '—'}</span>
                                <span className="text-gray-400"> {[s.from_city, s.from_state].filter(Boolean).join(', ')}</span>
                                <span className="mx-1 text-gray-400">→</span>
                                <span className="font-medium">{s.to_name || '—'}</span>
                                <span className="text-gray-400"> {[s.to_city, s.to_state].filter(Boolean).join(', ')}</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {s.created_at ? new Date(s.created_at).toLocaleDateString() : ''} • {s.carrier || ''} {s.service_name || ''}
                                {s.tracking_number ? ` • ${s.tracking_number}` : ''}
                              </div>
                            </div>
                            <div className="ml-3 flex-shrink-0 text-right">
                              <div className="text-sm text-gray-900">${Number(s.amount || 0).toFixed(2)}</div>
                              <div className="text-xs text-gray-400">{prettyType(s.status || s.fulfillment_status || '')}</div>
                            </div>
                          </div>
                          {expanded && (
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs bg-gray-50 p-2 rounded">
                              <div>
                                <p className="font-semibold text-gray-600">Ship from</p>
                                {fmtAddressLines(s.from_address, s.from_name).map((l, i) => (
                                  <p key={i} className="text-gray-700">{l}</p>
                                ))}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-600">Ship to</p>
                                {fmtAddressLines(s.to_address, s.to_name).map((l, i) => (
                                  <p key={i} className="text-gray-700">{l}</p>
                                ))}
                              </div>
                              <div className="sm:col-span-2 text-gray-500 pt-1 border-t border-gray-200">
                                Order {s.public_order_id || s.id}
                                {s.tracking_url ? (
                                  <> • <a href={s.tracking_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline" onClick={(e) => e.stopPropagation()}>Track</a></>
                                ) : null}
                                {s.label_url ? (
                                  <> • <a href={s.label_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline" onClick={(e) => e.stopPropagation()}>Label</a></>
                                ) : null}
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Account status + hold / verification control */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">Account Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${ACCOUNT_STATUS_STYLES[status] || ACCOUNT_STATUS_STYLES.active}`}>
                    {prettyType(status)}
                  </span>
                </div>

                {status !== 'active' && detail?.hold_reason && (
                  <p className="mt-2 text-sm text-gray-600">
                    Reason: {detail.hold_reason}
                    {detail.held_by ? ` • by ${detail.held_by}` : ''}
                    {detail.held_at ? ` • ${new Date(detail.held_at).toLocaleString()}` : ''}
                  </p>
                )}

                {can(role, 'support') ? (
                  status === 'active' ? (
                    <div className="mt-3 space-y-2">
                      <input
                        type="text"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Internal reason (not shown to the customer)"
                        className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <button
                        onClick={handleHold}
                        disabled={holdBusy}
                        className="min-h-[44px] px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
                      >
                        {holdBusy ? 'Working…' : 'Hold account — require verification'}
                      </button>
                      <p className="text-xs text-gray-500">
                        Blocks new label purchases immediately. The customer sees a "verify your account" message; this reason stays internal (staff only).
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={handleUnhold}
                      disabled={holdBusy}
                      className="mt-3 min-h-[44px] px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {holdBusy ? 'Working…' : 'Release hold — reactivate account'}
                    </button>
                  )
                ) : (
                  <p className="mt-2 text-xs text-gray-400">Requires the support role to change.</p>
                )}

                {can(role, 'finance') && (
                    <div className="mt-3 border-t border-gray-200 pt-3">
                      <p className="text-xs font-semibold text-gray-700">Cancel outstanding labels</p>
                      {!voidResult ? (
                        <button
                          onClick={() => handleVoidUnused(true)}
                          disabled={voidBusy}
                          className="mt-2 min-h-[44px] px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {voidBusy ? 'Checking…' : 'Count voidable labels'}
                        </button>
                      ) : voidResult.dryRun ? (
                        <>
                          <p className="mt-2 text-sm text-gray-800">
                            {voidResult.candidates} label{voidResult.candidates === 1 ? '' : 's'} can be cancelled.
                          </p>
                          <button
                            onClick={() => handleVoidUnused(false)}
                            disabled={voidBusy || voidResult.candidates === 0}
                            className="mt-2 min-h-[44px] px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                          >
                            {voidBusy ? 'Voiding…' : `Void ${voidResult.candidates} label${voidResult.candidates === 1 ? '' : 's'} & email customer`}
                          </button>
                        </>
                      ) : (
                        <p className="mt-2 text-sm text-gray-800">
                          Voided {voidResult.voided} of {voidResult.candidates}
                          {voidResult.skippedCount ? ` (${voidResult.skippedCount} skipped)` : ''}.{' '}
                          {voidResult.customerNotified ? 'Customer emailed.' : 'Customer NOT emailed — check logs.'}
                        </p>
                      )}
                      {voidError && <p className="mt-2 text-sm text-red-700">{voidError}</p>}
                      <p className="mt-2 text-xs text-gray-500">
                        Cancels the carrier labels and emails the customer one consolidated notice. No money moves here — each refund is released automatically once the carrier confirms its void.
                      </p>
                    </div>
                )}

                {can(role, 'support') && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-700">Request account verification</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Emails the customer a high-volume verification request (fraud / chargeback screening), confirming cardholder identity + business legitimacy.
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <label className="text-xs text-gray-600 flex items-center gap-1">
                        Respond within
                        <input type="number" min="1" max="30" value={verifyDeadline}
                          onChange={(e) => setVerifyDeadline(e.target.value)}
                          className="w-16 border border-gray-300 rounded px-2 py-1 text-sm" />
                        days
                      </label>
                      <label className="text-xs text-gray-600 flex items-center gap-1">
                        <input type="checkbox" checked={verifyHold} onChange={(e) => setVerifyHold(e.target.checked)} />
                        also place on hold
                      </label>
                      <button onClick={handleRequestVerification} disabled={verifyBusy}
                        className="min-h-[40px] px-4 py-2 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 disabled:opacity-50">
                        {verifyBusy ? 'Sending…' : 'Send verification request'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Activity timeline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Activity Timeline</label>
                {activityLoading ? (
                  <div className="flex items-center text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                    Loading activity…
                  </div>
                ) : activity.length === 0 ? (
                  <p className="text-sm text-gray-500">No recorded activity.</p>
                ) : (
                  <ul className="max-h-64 overflow-y-auto border border-gray-100 rounded-md divide-y divide-gray-100">
                    {activity.map((a, i) => (
                      <li key={`${a.event_type}-${a.occurred_at}-${i}`} className="px-3 py-2">
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_STYLES[a.event_category] || CATEGORY_STYLES.other}`}>
                              {prettyType(a.event_type)}
                            </span>
                            <span className="ml-2 text-sm text-gray-800">{a.description}</span>
                            <ClientContextLine row={a} />
                          </div>
                          <time className="ml-3 flex-shrink-0 text-xs text-gray-400 whitespace-nowrap">
                            {a.occurred_at ? new Date(a.occurred_at).toLocaleString() : ''}
                          </time>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="min-h-[44px] px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !customers) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
        <div className="flex gap-2">
          {can(role, 'finance') && (
            <button
              onClick={handleExport}
              className="inline-flex items-center min-h-[44px] px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Export CSV
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center min-h-[44px] px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <ArrowPathIcon className={`-ml-1 mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Customers Table — desktop */}
      <div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredCustomers.length > 0 ? (
            filteredCustomers.map((customer) => (
              <li key={customer.id}>
                <div className="px-4 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <UserIcon className="h-6 w-6 text-gray-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">
                          {customer.name || 'Unknown Customer'}
                        </p>
                        <AccountStatusBadge status={customer.account_status} />
                      </div>
                      <div className="flex items-center space-x-4 mt-1">
                        {customer.email && (
                          <div className="flex items-center text-sm text-gray-500">
                            <EnvelopeIcon className="h-4 w-4 mr-1" />
                            {customer.email}
                          </div>
                        )}
                        {customer.phone && (
                          <div className="flex items-center text-sm text-gray-500">
                            <PhoneIcon className="h-4 w-4 mr-1" />
                            {customer.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {customer.total_orders || 0} orders
                      </p>
                      <p className="text-sm text-gray-500">
                        ${customer.total_spent || '0.00'} spent
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedCustomer(customer)}
                      className="inline-flex items-center min-h-[44px] px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View
                    </button>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li className="px-4 py-8 text-center">
              <div className="text-gray-500">
                {searchTerm ? 'No customers found matching your search.' : 'No customers found.'}
              </div>
            </li>
          )}
        </ul>
      </div>

      {/* Customers — mobile stacked cards */}
      <div className="md:hidden space-y-3">
        {filteredCustomers.length > 0 ? (
          filteredCustomers.map((customer) => (
            <div key={customer.id} className="bg-white shadow rounded-md p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10">
                  <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                    <UserIcon className="h-6 w-6 text-gray-600" />
                  </div>
                </div>
                <p className="ml-3 text-sm font-medium text-gray-900 min-w-0 break-words">
                  {customer.name || 'Unknown Customer'}
                </p>
                <AccountStatusBadge status={customer.account_status} />
              </div>

              <div className="mt-3 space-y-1.5">
                {customer.email && (
                  <div className="flex items-start text-sm">
                    <span className="text-gray-400 w-20 flex-shrink-0">Email</span>
                    <span className="text-gray-700 break-all">{customer.email}</span>
                  </div>
                )}
                {customer.phone && (
                  <div className="flex items-start text-sm">
                    <span className="text-gray-400 w-20 flex-shrink-0">Phone</span>
                    <span className="text-gray-700 break-all">{customer.phone}</span>
                  </div>
                )}
                <div className="flex items-start text-sm">
                  <span className="text-gray-400 w-20 flex-shrink-0">Orders</span>
                  <span className="text-gray-700">{customer.total_orders || 0}</span>
                </div>
                <div className="flex items-start text-sm">
                  <span className="text-gray-400 w-20 flex-shrink-0">Spent</span>
                  <span className="text-gray-700">${customer.total_spent || '0.00'}</span>
                </div>
              </div>

              <div className="mt-3">
                <button
                  onClick={() => setSelectedCustomer(customer)}
                  className="inline-flex w-full items-center justify-center min-h-[44px] px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  View
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white shadow rounded-md px-4 py-8 text-center text-gray-500">
            {searchTerm ? 'No customers found matching your search.' : 'No customers found.'}
          </div>
        )}
      </div>

      {/* Customer Modal */}
      {selectedCustomer && (
        <CustomerModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
    </div>
  );
};

export default Customers;
